'use client';

import { useEffect } from 'react';
import { logger } from '@/utils/logger';

interface GoogleAnalyticsProps {
  measurementId: string;
}

// We don't need to redeclare these since they're already in global.d.ts
// Just using the existing definitions

export function GoogleAnalytics({ measurementId }: GoogleAnalyticsProps) {
  // Check if we're in development mode (must be before early return for hooks rules)
  const isDevelopment = import.meta.env.MODE === 'development' ||
                       window.location.hostname === 'localhost' ||
                       window.location.hostname === '127.0.0.1';

  useEffect(() => {
    // Don't run if no measurement ID is provided
    if (!measurementId || measurementId.trim() === '') {
      return;
    }

    // This ensures the script only runs on the client-side
    const addGAScript = () => {
      try {
        // In development, log instead of loading actual script if GTM is blocked
        if (isDevelopment) {
          logger.debug('🔧 Development mode: Google Analytics would be loaded with ID:', measurementId);
          logger.debug('🔧 To test GA in dev, ensure www.googletagmanager.com is accessible');
          
          // Create mock gtag for development testing
          window.dataLayer = window.dataLayer || [];
          window.gtag = function(...args: any[]) {
            logger.debug('🔧 Mock GA event:', args);
            if (window.dataLayer) {
              window.dataLayer.push(args);
            }
          };
          
          // Mock the config call
          window.gtag('js', new Date());
          window.gtag('config', measurementId);
          return;
        }

        // Check if script already exists to prevent duplicates
        const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
        if (existingScript) {
          logger.debug('Google Analytics script already loaded');
          return;
        }

        // Create the script element
        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        script.async = true;
        
        // Add error handling
        script.onerror = (error) => {
          logger.error('Failed to load Google Analytics script:', error);
          logger.error('This might be due to ad blockers or network restrictions');
        };
        
        script.onload = () => {
          logger.debug('Google Analytics script loaded successfully');
        };
        
        document.head.appendChild(script);
        
        // Set up the dataLayer and gtag function
        window.dataLayer = window.dataLayer || [];
        
        // Implement gtag exactly as Google Analytics does
        window.gtag = function(...args: any[]) {
          if (window.dataLayer) {
            window.dataLayer.push(args);
          }
        };
        
        // Initialize with standard commands
        window.gtag('js', new Date());
        window.gtag('config', measurementId);
      } catch (error) {
        logger.error('Error initializing Google Analytics:', error);
      }
    };
    
    // Run the function to add the script
    addGAScript();
    
    // Cleanup function
    return () => {
      if (!isDevelopment) {
        // Remove the script when the component unmounts (if needed)
        const scripts = document.querySelectorAll(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
        scripts.forEach(script => script.remove());
        
        // Clean up the window properties (only if no other instances are using them)
        if (scripts.length > 0) {
          delete window.gtag;
          delete window.dataLayer;
        }
      }
    };
  }, [measurementId, isDevelopment]);
  
  return null; // The script is added programmatically, so we don't need to render anything
} 